import type { UseQueryResult } from '@edreamsodigeo/frontend-api-client';
import { useQuery } from '@edreamsodigeo/frontend-api-client';
import { useLogFrontendApiError } from '@edreamsodigeo/frontend-core';

import type {
  GetShoppingSummaryItineraryQuery,
  GetShoppingSummaryItineraryQueryVariables,
} from '@/generated/graphql';
import { SHOPPING_SUMMARY_ITINERARY } from '@/clientState/providers/operations/shoppingSummaryItinerary';
import useItineraryFromUrl from '@/clientState/hooks/useItineraryFromUrl';
import { useMetaFlowProvider } from '@/clientState/providers/MetaFlowProvider';

export interface ShoppingSummaryItineraryData {
  products: GetShoppingSummaryItineraryQuery['getShoppingSummary']['products'];
}

interface ShoppingSummaryItineraryExtendedData
  extends ShoppingSummaryItineraryData {
  totalNumPassengers: GetShoppingSummaryItineraryQuery['getShoppingSummary']['totalNumPassengers'];
}

interface UseShoppingSummaryItineraryQuery {
  errors: UseQueryResult<
    GetShoppingSummaryItineraryQuery,
    GetShoppingSummaryItineraryQueryVariables
  >['errors'];
  loading: UseQueryResult<
    GetShoppingSummaryItineraryQuery,
    GetShoppingSummaryItineraryQueryVariables
  >['loading'];
  data: ShoppingSummaryItineraryExtendedData | undefined;
}

export const useShoppingSummaryItinerary =
  (): UseShoppingSummaryItineraryQuery => {
    const {
      fareItineraryKey = '',
      feeSelected,
      searchId = '',
      segmentKeys = [],
    } = useItineraryFromUrl();
    const isMeta = useMetaFlowProvider();
    const result = useQuery<
      GetShoppingSummaryItineraryQuery,
      GetShoppingSummaryItineraryQueryVariables
    >(SHOPPING_SUMMARY_ITINERARY, {
      variables: {
        request: {
          fareItineraryKey,
          feeSelected,
          isMeta,
          searchId: String(searchId),
          segmentKeys,
        },
      },
      skip: !fareItineraryKey || !searchId,
    });
    useLogFrontendApiError(result);
    const { data, ...rest } = result;

    return {
      data: {
        products: data?.getShoppingSummary.products || [],
        totalNumPassengers: data?.getShoppingSummary.totalNumPassengers || 0,
      },
      ...rest,
    };
  };
